import React, { useState } from "react";
import { Link } from "react-router-dom";
import ds from "../../design/design-system.json";

export default function DesignSystem() {
  const [tab, setTab] = useState("tokens");
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-canvas text-fg">
      <header className="border-b border-border bg-surface px-4 py-4 md:px-8">
        <div className="mx-auto flex max-w-content flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary">{ds.meta.name}</p>
            <h1 className="font-display text-hero font-bold tracking-tight">Design system</h1>
            <p className="mt-1 max-w-2xl text-sm text-fg-secondary">{ds.meta.inspiration}</p>
          </div>
          <Link to="/" className="rounded-ds border border-border bg-canvas-subtle px-4 py-2 text-sm font-medium hover:bg-canvas-muted focus:outline-none focus-visible:shadow-focus">
            ← App
          </Link>
        </div>
      </header>

      <div className="mx-auto grid max-w-content gap-8 px-4 py-8 lg:grid-cols-[220px_1fr] md:px-8">
        <nav className="h-fit rounded-ds-xl border border-border bg-surface p-2 text-sm shadow-card" aria-label="Design system sections">
          {[
            { id: "tokens", label: "Tokens" },
            { id: "components", label: "Components" },
            { id: "json", label: "Raw JSON" },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setTab(s.id)}
              className={`mb-1 w-full rounded-ds px-3 py-2 text-left font-medium focus:outline-none focus-visible:shadow-focus ${
                tab === s.id ? "bg-accent-muted text-fg" : "text-fg-secondary hover:bg-canvas-subtle"
              }`}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <div>
          {tab === "tokens" && (
            <section className="space-y-8 animate-ds-fade-in">
              <div>
                <h2 className="mb-3 text-lg font-bold">Colors</h2>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(ds.colors.canvas).map(([k, v]) => (
                    <div key={k} className="card overflow-hidden p-0">
                      <div className="h-14" style={{ background: v }} />
                      <div className="p-3 text-xs">
                        <div className="font-semibold">canvas.{k}</div>
                        <div className="font-mono text-fg-tertiary">{v}</div>
                      </div>
                    </div>
                  ))}
                  <div className="card overflow-hidden p-0">
                    <div className="h-14 bg-accent" />
                    <div className="p-3 text-xs">
                      <div className="font-semibold">accent.primary</div>
                      <div className="font-mono text-fg-tertiary">{ds.colors.accent.primary}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h2 className="mb-3 text-lg font-bold">Typography</h2>
                <p className="mb-2 text-xs text-fg-tertiary">Sans: {ds.typography.fontSans}</p>
                <p className="mb-4 text-xs text-fg-tertiary">Mono: {ds.typography.fontMono}</p>
                <div className="space-y-2 rounded-ds-xl border border-border bg-surface p-4">
                  {Object.entries(ds.typography.sizes).map(([k, v]) => (
                    <p key={k} className="font-sans text-fg" style={{ fontSize: v }}>
                      {k} — The quick brown fox ({v})
                    </p>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="mb-3 text-lg font-bold">Spacing & radius</h2>
                <p className="text-sm text-fg-secondary">Unit {ds.spacing.unitPx}px · card padding {ds.spacing.cardPadding}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(ds.radius).map(([k, v]) => (
                    <div key={k} className="flex flex-col items-center gap-1 border border-border bg-surface p-3 text-xs">
                      <div className="h-10 w-10 border-2 border-accent bg-accent-muted" style={{ borderRadius: v }} />
                      {k}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {tab === "components" && (
            <section className="animate-ds-fade-in space-y-8">
              <div>
                <h2 className="mb-3 text-lg font-bold">Buttons</h2>
                <div className="flex flex-wrap gap-3">
                  <button type="button" className="rounded-ds bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover focus:outline-none focus-visible:shadow-focus">
                    Primary
                  </button>
                  <button type="button" className="rounded-ds border border-border bg-surface px-4 py-2 text-sm font-medium hover:bg-canvas-subtle focus:outline-none focus-visible:shadow-focus">
                    Secondary
                  </button>
                  <button type="button" className="rounded-ds px-4 py-2 text-sm text-fg-secondary hover:bg-canvas-subtle focus:outline-none focus-visible:shadow-focus">
                    Ghost
                  </button>
                </div>
              </div>
              <div>
                <h2 className="mb-3 text-lg font-bold">Cards</h2>
                <div className="card max-w-md p-4">
                  <div className="text-sm font-semibold text-fg">Card title</div>
                  <p className="mt-1 text-xs text-fg-secondary">Border, radius-xl, shadow-sm → md on hover (global .card).</p>
                </div>
              </div>
              <div>
                <h2 className="mb-3 text-lg font-bold">Tabs</h2>
                <div className="inline-flex rounded-ds border border-border bg-canvas-subtle p-1">
                  {["One", "Two", "Three"].map((l, i) => (
                    <button
                      key={l}
                      type="button"
                      className={`rounded-ds px-3 py-1.5 text-xs font-medium ${i === 0 ? "bg-surface text-fg shadow-sm" : "text-fg-secondary"}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="mb-3 text-lg font-bold">Badges & inputs</h2>
                <div className="mb-3 flex flex-wrap gap-2">
                  <span className="rounded-full bg-success/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success">Live</span>
                  <span className="rounded-full bg-warning/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">Warn</span>
                  <span className="rounded-full bg-danger/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-danger">Error</span>
                </div>
                <input type="text" placeholder="Search…" className="w-full max-w-sm rounded-ds border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus-visible:shadow-focus" />
              </div>
              <div>
                <h2 className="mb-3 text-lg font-bold">Modal</h2>
                <button type="button" onClick={() => setModalOpen(true)} className="rounded-ds bg-accent px-4 py-2 text-sm font-semibold text-white">
                  Open sample modal
                </button>
                {modalOpen && (
                  <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="ds-modal-title">
                    <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close" onClick={() => setModalOpen(false)} />
                    <div className="relative z-[201] w-full max-w-narrow rounded-ds-2xl border border-border bg-surface p-6 shadow-modal">
                      <h3 id="ds-modal-title" className="text-lg font-bold">
                        Sample modal
                      </h3>
                      <p className="mt-2 text-sm text-fg-secondary">Backdrop + radius-2xl + shadow-lg per design-system.json components.modal.</p>
                      <button type="button" onClick={() => setModalOpen(false)} className="mt-4 rounded-ds bg-accent px-4 py-2 text-sm font-semibold text-white">
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {tab === "json" && (
            <pre className="animate-ds-fade-in overflow-auto rounded-ds-xl border border-border bg-canvas-subtle p-4 text-[11px] leading-relaxed text-fg-secondary">{JSON.stringify(ds, null, 2)}</pre>
          )}
        </div>
      </div>
    </div>
  );
}
